import localforage from 'localforage';

import { BrowserStorage } from '../browserstorage/BrowserStorage';
import { SDK } from '../index';

/**
 * This service offers persistence of data in the browser.
 */
export class BrowserData extends BrowserStorage {
  /**
   * @hidden
   */
  static install(instance: SDK) {
    instance['_browserData'] = new BrowserData();
  }

  /**
   * @hidden
   */
  constructor() {
    super();
  }

  /**
   * Set an item in the browser's local storage.
   *
   * ```js example
   * dory.BrowserData.setItem('somekey', 'some value')
   *   .then(function (value) {
   *     console.log(value);
   *   })
   *   .catch(function (error) {
   *     console.error(error);
   *   });
   * ```
   *
   * @param key The key to store the item under.
   * @param value The value to store.
   * @returns A Promise that resolves when the item has been set.
   */
  public setItem(key: string, value: any): Promise<any> {
    return localforage.setItem(key, value);
  }

  /**
   * Get an item from the browser's local storage.
   *
   * ```js example
   * dory.BrowserData.getItem('somekey')
   *   .then(function (value) {
   *     console.log(value);
   *   })
   *   .catch(function (error) {
   *     console.error(error);
   *   });
   * ```
   *
   * @param key The key of the item to retrieve.
   * @returns A Promise that resolves with the item value, or null if the item doesn't exist.
   */
  public getItem(key: string): Promise<any> {
    return localforage.getItem(key);
  }

  /**
   * Remove an item from the browser's local storage.
   *
   * ```js example
   * dory.BrowserData.removeItem('somekey')
   *   .then(function () {
   *     console.log('Key is cleared!');
   *   })
   *   .catch(function (error) {
   *     console.error(error);
   *   });
   * ```
   *
   * @param key The key of the item to remove.
   * @returns A Promise that resolves when the item has been removed.
   */
  public removeItem(key: string): Promise<any> {
    return localforage.removeItem(key);
  }

  /**
   * Clear all items from the browser's local storage.
   *
   * ```js example
   * dory.BrowserData.clear()
   *   .then(function () {
   *     console.log('Database is now empty.');
   *   })
   *   .catch(function (error) {
   *     console.error(error);
   *   });
   * ```
   *
   * @returns A Promise that resolves when all items have been cleared.
   */
  public clear(): Promise<void> {
    return localforage.clear();
  }
}
